import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { Button, Drawer, Empty, Input, Message, Modal, Table, Tag, Tooltip, Typography } from "@arco-design/web-react";
import { IconDelete, IconEdit, IconPlus, IconUserGroup } from "@arco-design/web-react/icon";

import { FetchApi, DeleteApi, OverwriteApi, PostApi } from "../Api/AuthenticatedApi";
import SimpleBreadcrumbs from "../Components/Breadcrumbs/SimpleBreadcrumbs";
import FilterBuilder, { FilterCounts } from "../Components/ContactFilter/FilterBuilder";
import useFilterOptions from "../Components/ContactFilter/useFilterOptions";
import { emptyFilter, filterText, filterToParam, forServer, fromLegacy } from "../Components/ContactFilter/contactFilter";
import Content from "../Layout/Content";
import LoadingState from "../Layout/LoadingState";
import SummaryCard from "../Layout/SummaryCard";

const { Text } = Typography;

// segmentFilter is what a segment filters by as a rule-based filter: its own,
// or (for segments saved before rules) one made from its list / status / tag.
export function segmentFilter(seg) {
  return (seg.filter && seg.filter.groups && seg.filter.groups.length ? seg.filter : null) ||
    fromLegacy({ listId: seg.contact_list_filter_id, status: seg.status_filter, tagId: seg.tag_filter_id });
}

// The /contacts URL that shows a segment's matches.
export function contactsUrlForSegment(seg) {
  const params = new URLSearchParams();
  const f = filterToParam(segmentFilter(seg));
  if (f) params.set("filter", f);
  if (seg.search_term) params.set("searchTerm", seg.search_term);
  const qs = params.toString();
  return "/contacts" + (qs ? "?" + qs : "");
}

// SegmentEditor creates or edits a segment: its name, search text and rules,
// with live counts.
function SegmentEditor({ segment, options, onClose, onSaved }) {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const editing = segment && segment.id;
  const [name, setName] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState(emptyFilter());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!segment) return;
    setName(segment.name || "");
    setSearch(segment.search_term || "");
    setFilter(segmentFilter(segment) || emptyFilter());
  }, [segment]);

  const save = () => {
    if (!name.trim()) {
      Message.warning("Give the segment a name");
      return;
    }
    setSaving(true);
    const body = { name: name.trim(), search_term: search.trim(), filter: forServer(filter, options.customFieldDefs) };
    const done = (saved) => {
      setSaving(false);
      Message.success(editing ? "Segment saved" : "Segment created");
      onSaved(saved);
    };
    const failed = (err) => {
      setSaving(false);
      Message.error((err && err.message) || "Failed to save the segment");
    };
    if (editing) {
      OverwriteApi(getAccessTokenSilently, "/api/segments/" + segment.id, done, () => navigate("/login"), failed, body);
    } else {
      PostApi(getAccessTokenSilently, "/api/segments/", done, () => navigate("/login"), failed, body);
    }
  };

  return (
    <Drawer
      width={680}
      title={editing ? "Edit segment" : "New segment"}
      visible={!!segment}
      onCancel={onClose}
      footer={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {segment && <FilterCounts filter={filter} searchTerm={search} customFieldDefs={options.customFieldDefs} />}
          <div style={{ flex: 1 }} />
          <Button onClick={onClose}>Cancel</Button>
          <Button type="primary" loading={saving} onClick={save}>Save</Button>
        </div>
      }
    >
      <div style={{ display: "grid", gap: 12 }}>
        <div>
          <Text style={{ fontSize: 12, color: "var(--color-text-3)" }}>Name</Text>
          <Input aria-label="Segment name" placeholder="e.g. Engaged VIPs" value={name} onChange={setName} />
        </div>
        <div>
          <Text style={{ fontSize: 12, color: "var(--color-text-3)" }}>Name or email contains (optional)</Text>
          <Input aria-label="Name or email contains" allowClear value={search} onChange={setSearch} />
        </div>
        <FilterBuilder value={filter} onChange={setFilter} options={options} />
        <Text type="secondary" style={{ fontSize: 12 }}>
          A segment stays up to date: whoever matches when it's used is included.
        </Text>
      </div>
    </Drawer>
  );
}

export default function Segments() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const options = useFilterOptions();
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refresh, setRefresh] = useState(true);
  const [editing, setEditing] = useState(null); // a segment, {} for a new one, or null

  useEffect(() => {
    if (!refresh) return;
    FetchApi(getAccessTokenSilently, "/api/segments/",
      (data) => { setSegments(data || []); setLoading(false); setRefresh(false); },
      () => navigate("/login"),
      () => { setLoading(false); setRefresh(false); Message.error("Failed to load segments"); });
  }, [getAccessTokenSilently, navigate, refresh]);

  const handleDelete = (seg) => {
    Modal.confirm({
      title: `Delete "${seg.name}"?`,
      content: "Broadcasts and workflows that target it will have no audience until you pick another. Contacts aren't changed.",
      okText: "Delete",
      okButtonProps: { status: "danger" },
      onOk: () => DeleteApi(getAccessTokenSilently, "/api/segments/" + seg.id,
        () => { Message.success("Segment deleted"); setRefresh(true); },
        () => navigate("/login"),
        () => Message.error("Failed to delete segment"),
        {}),
    });
  };

  const describe = (seg) => {
    const rules = filterText(segmentFilter(seg), options.names, options.customFieldDefs);
    const search = seg.search_term ? `name or email contains "${seg.search_term}"` : "";
    return [search, rules].filter(Boolean).join(" AND ") || "All contacts";
  };

  const columns = [
    { title: "Name", dataIndex: "name", render: (name) => <strong>{name}</strong> },
    {
      title: "Contacts",
      // Everyone matching, and how many of them a broadcast to it reaches
      // (the rest bounced, unsubscribed, or haven't confirmed).
      render: (_, seg) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tag color="arcoblue">{(seg.member_count || 0).toLocaleString()} match</Tag>{" "}
          <Tooltip content="Confirmed, still subscribed, and not bounced: who a broadcast to this segment reaches">
            <span style={{ color: "var(--color-text-3)", fontSize: 12 }}>
              {(seg.mailable_count || 0).toLocaleString()} can be mailed
            </span>
          </Tooltip>
        </span>
      ),
    },
    {
      title: "Rules",
      render: (_, seg) => <span style={{ color: "var(--color-text-2)", fontSize: 12 }}>{describe(seg)}</span>,
    },
    {
      title: "",
      render: (_, seg) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Button size="small" type="outline" icon={<IconUserGroup />} onClick={() => navigate(contactsUrlForSegment(seg))}>
            View contacts
          </Button>
          <Button size="small" shape="circle" type="text" aria-label={`Edit ${seg.name}`} icon={<IconEdit />} onClick={() => setEditing(seg)} />
          <Button size="small" shape="circle" type="text" aria-label={`Delete ${seg.name}`} icon={<IconDelete />} onClick={() => handleDelete(seg)} />
        </div>
      ),
    },
  ];

  if (loading) {
    return <LoadingState />;
  }

  return (
    <Content>
      <SimpleBreadcrumbs elements={[{ name: "Segments" }]} />
      <div style={{ display: "flex", justifyContent: "flex-end", margin: "8px 0" }}>
        <Button type="primary" icon={<IconPlus />} onClick={() => setEditing({})}>New segment</Button>
      </div>
      <SummaryCard title="Segments" subTitle="Saved contact filters you can reuse and target">
        <Table
          size="small"
          columns={columns}
          data={segments}
          rowKey="id"
          pagination={false}
          border={false}
          noDataElement={
            <Empty description="No segments yet">
              <Button type="primary" style={{ marginTop: 12 }} onClick={() => setEditing({})}>
                Create your first segment
              </Button>
            </Empty>
          }
        />
      </SummaryCard>
      <SegmentEditor
        segment={editing}
        options={options}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); setRefresh(true); }}
      />
    </Content>
  );
}
